import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { UserRound, LogOut, Bell, ArrowLeft } from 'lucide-react';
import api from '../services/api';
import socketService from '../services/socket';
import './Navbar.css';

const { Home01Icon } = require("hugeicons-react");

interface NavbarProps {
  isLoggedIn: boolean;
  onLogout: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ isLoggedIn, onLogout }) => {
  const [user, setUser] = useState<any>(null);
  const [showSearchOverlay, setShowSearchOverlay] = useState(false);
  const [showHamburgerDropdown, setShowHamburgerDropdown] = useState(false);
  const [showTopHamburgerDropdown, setShowTopHamburgerDropdown] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Lock/unlock body scroll when logout modal is opened/closed
  useEffect(() => {
    if (showLogoutModal) {
      // Disable scrolling
      document.body.style.overflow = 'hidden';
    } else {
      // Re-enable scrolling
      document.body.style.overflow = 'unset';
    }

    // Cleanup function to ensure scrolling is re-enabled when component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showLogoutModal]);

  // Lock/unlock body scroll when search overlay is opened/closed on mobile
  useEffect(() => {
    const isMobile = window.innerWidth <= 768;

    if (showSearchOverlay && isMobile) {
      // Disable scrolling on mobile when search overlay is open
      document.body.style.overflow = 'hidden';
    } else {
      // Re-enable scrolling
      document.body.style.overflow = 'unset';
    }

    // Cleanup function to ensure scrolling is re-enabled when component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showSearchOverlay]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [searchOffset, setSearchOffset] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchResultsRef = useRef<HTMLDivElement>(null);
  const hamburgerDropdownRef = useRef<HTMLDivElement>(null);
  const topHamburgerDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isLoggedIn) {
      const savedUser = localStorage.getItem('user');
      if (savedUser) {
        setUser(JSON.parse(savedUser));
      }
    }
  }, [isLoggedIn]);

  // Socket connection and notification handling
  useEffect(() => {
    if (isLoggedIn) {
      // Connect to socket
      socketService.connect();

      // Set up socket event listeners
      const handleNewNotification = (notification: any) => {
        // Increment unread count
        setUnreadCount(prev => prev + 1);
      };

      const handleDeletedNotification = (data: { id: string }) => {
        // Decrement unread count
        setUnreadCount(prev => Math.max(0, prev - 1));
      };

      const handleUnreadCountUpdate = (data: { count: number }) => {
        setUnreadCount(data.count);
      };

      socketService.onNotificationNew(handleNewNotification);
      socketService.onNotificationDeleted(handleDeletedNotification);
      socketService.onUnreadCountUpdate(handleUnreadCountUpdate);

      return () => {
        socketService.offNotificationNew(handleNewNotification);
        socketService.offNotificationDeleted(handleDeletedNotification);
        socketService.offUnreadCountUpdate(handleUnreadCountUpdate);
      };
    } else {
      // Disconnect socket when logged out
      socketService.disconnect();
    }
  }, [isLoggedIn]);

  // Fetch unread notifications count with window focus detection (fallback)
  useEffect(() => {
    const fetchUnreadCount = async () => {
      if (!isLoggedIn) return;

      try {
        const response = await api.get('/notifications/unread-count');
        setUnreadCount(response.data.count);
      } catch (error) {
        // Error handled silently
      }
    };

    // Initial fetch
    fetchUnreadCount();

    // Set up 90-second polling interval as fallback (only when socket is not connected)
    const interval = setInterval(() => {
      if (!socketService.isConnected()) {
        fetchUnreadCount();
      }
    }, 90000);

    // Listen for notifications read events
    const handleNotificationsRead = () => {
      fetchUnreadCount();
    };

    window.addEventListener('notificationsRead', handleNotificationsRead);

    return () => {
      clearInterval(interval);
      window.removeEventListener('notificationsRead', handleNotificationsRead);
    };
  }, [isLoggedIn]);

  // Window focus detection for notifications
  useEffect(() => {
    const fetchUnreadCount = async () => {
      if (!isLoggedIn) return;

      try {
        const response = await api.get('/notifications/unread-count');
        setUnreadCount(response.data.count);
      } catch (error) {
        // Error handled silently
      }
    };

    const handleWindowFocus = () => {
      // Fetch notifications immediately when user returns to tab
      fetchUnreadCount();
    };

    const handleWindowBlur = () => {
      // Optional: Could pause polling here, but we'll keep it simple
    };

    window.addEventListener('focus', handleWindowFocus);
    window.addEventListener('blur', handleWindowBlur);

    return () => {
      window.removeEventListener('focus', handleWindowFocus);
      window.removeEventListener('blur', handleWindowBlur);
    };
  }, [isLoggedIn]);

  // Listen for profile picture updates
  useEffect(() => {
    const handleProfilePictureUpdate = (event: CustomEvent) => {
      setUser((prevUser: any) => ({
        ...prevUser,
        profile_picture_url: event.detail.profile_picture_url
      }));
    };

    window.addEventListener('profilePictureUpdated', handleProfilePictureUpdate as EventListener);

    return () => {
      window.removeEventListener('profilePictureUpdated', handleProfilePictureUpdate as EventListener);
    };
  }, []);




  // Search for users
  const handleSearch = async (query: string, offset: number = 0, isLoadMore: boolean = false) => {
    if (!query.trim()) {
      setSearchResults([]);
      setIsSearching(false);
      setHasMore(true);
      setSearchOffset(0);
      return;
    }

    if (isLoadMore) {
      setIsLoadingMore(true);
    } else {
      setIsSearching(true);
    }

    try {
      const limit = 10;
      const response = await api.get(`/search?q=${encodeURIComponent(query)}&limit=${limit}&offset=${offset}`);
      const newUsers = response.data.users || [];

      if (isLoadMore) {
        // Filter out duplicates when loading more
        const existingIds = new Set(searchResults.map((user: any) => user.id));
        const uniqueNewUsers = newUsers.filter((user: any) => !existingIds.has(user.id));
        setSearchResults([...searchResults, ...uniqueNewUsers]);
      } else {
        setSearchResults(newUsers);
      }

      // Check if there are more results
      setHasMore(newUsers.length === limit);
      setSearchOffset(offset + newUsers.length);
    } catch (error) {
      if (!isLoadMore) {
        setSearchResults([]);
      }
    } finally {
      setIsSearching(false);
      setIsLoadingMore(false);
    }
  };

  // Update the search input onChange to trigger loading immediately
  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchQuery(newValue);
    setSearchOffset(0);
    setHasMore(true);

    // Start loading immediately when user types
    if (newValue.trim()) {
      setIsSearching(true);
    } else {
      setIsSearching(false);
      setSearchResults([]);
    }
  };

  // Debounced search - but loading state starts immediately
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (showSearchOverlay) {
        handleSearch(searchQuery, 0, false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, showSearchOverlay]);

  // Infinite scroll handler
  useEffect(() => {
    const searchResultsElement = searchResultsRef.current;
    if (!searchResultsElement) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = searchResultsElement;

      // Load more when user scrolls halfway through the content
      const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;
      if (scrollPercentage > 0.5 && hasMore && !isSearching && !isLoadingMore && searchQuery.trim()) {
        handleSearch(searchQuery, searchOffset, true);
      }
    };

    searchResultsElement.addEventListener('scroll', handleScroll);
    return () => searchResultsElement.removeEventListener('scroll', handleScroll);
  }, [searchOffset, hasMore, isSearching, isLoadingMore, searchQuery]);

  const handleCreateClick = () => {
    // Close search overlay if open
    setShowSearchOverlay(false);
    // Navigate to create page
    navigate('/create');
  };




  const handleSearchClick = () => {
    if (showSearchOverlay) {
      // If search is already open, close it
      setShowSearchOverlay(false);
      setSearchQuery('');
      setSearchResults([]);
    } else {
      // If search is closed, open it
      setShowSearchOverlay(true);
      setSearchQuery('');
      setSearchResults([]);
    }
  };

  const handleSearchClose = () => {
    setShowSearchOverlay(false);
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleUserClick = (username: string) => {
    setShowSearchOverlay(false);
    setSearchQuery('');
    setSearchResults([]);
    navigate(`/profile/${username}`);
  };


  const handleLogoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowSearchOverlay(false);
    if (location.pathname === '/feed') {
      // Clear saved scroll position and set flag to prevent restoration
      sessionStorage.removeItem('scrollPos-/feed');
      sessionStorage.setItem('logoClickRefresh', 'true');
      window.dispatchEvent(new CustomEvent('refreshFeed'));
      window.scrollTo(0, 0);
    }
    else if (location.pathname === '/following') {
      // Clear saved scroll position and set flag to prevent restoration
      sessionStorage.removeItem('scrollPos-/following');
      sessionStorage.setItem('logoClickRefresh', 'true');
      window.dispatchEvent(new CustomEvent('refreshFeed'));
      window.scrollTo(0, 0);
    }
    else {
      navigate('/feed'); // react-router-dom
    }
  };

  const handleHomeClick = (e: React.MouseEvent) => {
    e.preventDefault();
    // Close search overlay if open
    setShowSearchOverlay(false);

    // If already on feed page, refresh the feed (clear cache, fresh API call) and scroll to top
    if (location.pathname === '/feed') {
      // Clear saved scroll position and set flag to prevent restoration
      sessionStorage.removeItem('scrollPos-/feed');
      sessionStorage.setItem('logoClickRefresh', 'true');
      // Dispatch custom event to refresh feed
      window.dispatchEvent(new CustomEvent('refreshFeed'));
      // Scroll to top of the feed
      window.scrollTo(0, 0);
    } else {
      navigate('/feed'); // react-router-dom
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const handleHamburgerClick = () => {
    setShowHamburgerDropdown(!showHamburgerDropdown);
  };

  const handleBackClick = () => {
    // Always try to use browser's back functionality first
    if (window.history.length > 1) {
      window.history.back();
      return;
    }

    // Fallback to sessionStorage-based navigation
    const previousLocation = sessionStorage.getItem('previousLocation');

    // If no previous location is saved, default to root (which shows Feed for logged-in users)
    const targetPath = previousLocation || '/';

    // Clear the previous location to prevent navigation loops
    sessionStorage.removeItem('previousLocation');

    // Navigate back to the previous location
    navigate(targetPath);
  };
  const isOnProfilePage = () => {
    const path = location.pathname;
    return path.startsWith('/profile');
  };

  // Determine if back button should be shown
  const shouldShowBackButton = () => {
    const path = location.pathname;
    // Show back button for post detail pages and other user profiles (not own profile)
    return path.startsWith('/post/') || (path.startsWith('/profile/') && path !== '/profile') || path.startsWith('/settings') || path.startsWith('/legal') || path.startsWith('/privacy') || path.startsWith('/terms');
  };

  const handleLogoutClick = () => {
    setShowHamburgerDropdown(false);
    setShowTopHamburgerDropdown(false);
    setShowLogoutModal(true);
  };

  const handleLogoutConfirm = () => {
    setShowLogoutModal(false);
    onLogout();
  };

  const handleLogoutCancel = () => {
    setShowLogoutModal(false);
  };

  // Close hamburger dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (hamburgerDropdownRef.current && !hamburgerDropdownRef.current.contains(event.target as Node)) {
        setShowHamburgerDropdown(false);
      }
      if (topHamburgerDropdownRef.current && !topHamburgerDropdownRef.current.contains(event.target as Node)) {
        setShowTopHamburgerDropdown(false);
      }
    };

    if (showHamburgerDropdown || showTopHamburgerDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showHamburgerDropdown, showTopHamburgerDropdown]);

  if (!isLoggedIn || !user) {
    return null;
  }

  return (
    <>
      {/* Top navbar for small screens */}
      <div className="navbar-top">
        <div className="navbar-top-content">
          <div className="navbar-top-left">
            {shouldShowBackButton() && !showSearchOverlay && (
              <button onClick={handleBackClick} className="navbar-back-button">
                <ArrowLeft className="navbar-back-icon" />
              </button>
            )}
          </div>
          <div className="navbar-top-logo">
            <Link to="/feed" className="navbar-top-logo-link" onClick={handleLogoClick}>
              <img
                src="/mindcast_logo.svg"
                alt="Mindcast"
                className="navbar-top-logo-image"
              />
            </Link>
          </div>

          {/* Top hamburger container always rendered to preserve centering; button only on profile OR logout on settings */}
          <div className="navbar-top-hamburger-container" ref={topHamburgerDropdownRef}>
            {isOnProfilePage() ? (
              <button
                onClick={() => { setShowSearchOverlay(false); navigate('/settings'); }}
                className={`navbar-top-hamburger-button`}
                title="Settings"
              >
                <div className="navbar-hamburger-icon">
                  <div className="navbar-hamburger-line"></div>
                  <div className="navbar-hamburger-line"></div>
                </div>
              </button>
            ) : location.pathname.startsWith('/settings') ? (
              <button
                onClick={handleLogoutClick}
                className={`navbar-top-hamburger-button`}
                title="Log out"
              >
                <LogOut className="top-nav-logout" />
              </button>
            ) : null}
          </div>
        </div>
      </div>

      <div className="navbar-sidebar">
        {/* Logo */}
        <div className="navbar-sidebar-logo">
          <Link to="/feed" className="navbar-logo-link" onClick={handleLogoClick}>
            <img
              src="/mindcast_logo.svg"
              alt="Mindcast"
              className="navbar-logo-image"
            />
          </Link>
        </div>

        {/* Navigation Items */}
        <nav className="navbar-sidebar-nav">
          <Link
            to="/feed"
            className={`nav-item ${location.pathname === '/feed' || location.pathname === '/following' ? 'active' : ''}`}
            title="Home"
            onClick={handleHomeClick}
          >
            <Home01Icon className="nav-icon" />
          </Link>

          <button
            onClick={handleSearchClick}
            className={`nav-item ${showSearchOverlay ? 'active' : ''}`}
            title="Search Users"
          >
            <MagnifyingGlassIcon className="nav-icon" />
          </button>

          <button
            onClick={handleCreateClick}
            className={`nav-item create-button ${location.pathname === '/create' ? 'active' : ''}`}
            title="Create Post"
          >
            <PlusIcon className="nav-icon" />
          </button>

          <Link
            to="/notifications"
            className={`nav-item ${location.pathname === '/notifications' ? 'active' : ''}`}
            title="Notifications"
            onClick={() => setShowSearchOverlay(false)}
          >
            <Bell className="nav-icon" />
            {unreadCount > 0 && (
              <span className="notification-badge">
                {unreadCount > 99 ? '99' : unreadCount}
              </span>
            )}
          </Link>

          {/* User button - always show user icon */}
          <Link
            to={`/profile/${user?.username}`}
            className={`nav-item ${location.pathname === `/profile/${user?.username}` ? 'active' : ''}`}
            title="Profile"
            onClick={() => setShowSearchOverlay(false)}
          >
            <UserRound className="nav-icon" />
          </Link>
        </nav>

        {/* Bottom hamburger menu - only visible on large screens */}
        <div className="navbar-hamburger-container">
          <div className="navbar-hamburger" ref={hamburgerDropdownRef}>
            <button
              onClick={handleHamburgerClick}
              className={`nav-item navbar-hamburger-button ${showHamburgerDropdown ? 'active' : ''}`}
              title="Menu"
            >
              <div className="navbar-hamburger-icon">
                <div className="navbar-hamburger-line"></div>
                <div className="navbar-hamburger-line"></div>
              </div>
            </button>

            {/* Hamburger Dropdown Menu */}
            {showHamburgerDropdown && (
              <div className="navbar-hamburger-dropdown">
                <button
                  onClick={handleLogoutClick}
                  className="navbar-hamburger-dropdown-item"
                >
                  <span>Log out</span>
                </button>
              </div>
            )}
          </div>
        </div>

      </div>

      {/* Search Overlay */}
      {showSearchOverlay && (
        <>
          {/* Backdrop */}
          <div
            className="search-backdrop"
            onClick={handleSearchClose}
          />

          {/* Search Panel */}
          <div className={`search-overlay ${showSearchOverlay ? 'open' : ''}`}>
            <div className="search-header">
              <h2>Search</h2>
              <button
                onClick={handleSearchClose}
                className="search-close-button"
              >
                <XMarkIcon className="close-icon" />
              </button>
            </div>

            <div className="search-content">
              <div className="search-input-container">
                <MagnifyingGlassIcon className="search-input-icon" />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search for users..."
                  value={searchQuery}
                  onChange={handleSearchInputChange} // Use the new handler
                  className="search-input"
                />
                {searchQuery && (
                  <button
                    onClick={handleClearSearch}
                    className="search-clear-button"
                    type="button"
                  >
                    <XMarkIcon className="clear-icon" />
                  </button>
                )}
              </div>

              <div className="search-results" ref={searchResultsRef}>
                {isSearching ? (
                  <div className="results-section">
                    <h3 className="results-title">Users</h3>
                    <div className="navbar-search-skeleton">
                      {[...Array(8)].map((_, index) => (
                        <div key={index} className="navbar-search-skeleton-item">
                          <div className="navbar-search-skeleton-avatar"></div>
                          <div className="navbar-search-skeleton-content">
                            <div className="navbar-search-skeleton-username"></div>
                            <div className="navbar-search-skeleton-email"></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : searchQuery.trim() && searchResults.length === 0 ? (
                  <div className="results-section">
                    <h3 className="results-title">Users</h3>
                    <div className="no-results">
                      <p>No users found for "{searchQuery}"</p>
                    </div>
                  </div>
                ) : searchQuery.trim() ? (
                  <div className="results-section">
                    <h3 className="results-title">Users</h3>
                    {searchResults.map((searchUser) => (
                      <div
                        key={searchUser.id}
                        className="search-result-item"
                        onClick={() => handleUserClick(searchUser.username)}
                      >
                        <div className="search-user-avatar">
                          {searchUser.profile_picture_url ? (
                            <img
                              src={searchUser.profile_picture_url}
                              alt={`${searchUser.username}'s profile`}
                              className="search-user-avatar-image"
                            />
                          ) : (
                            <span className="search-user-avatar-text">
                              {searchUser.username[0].toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div className="search-user-info">
                          <span className="search-username">@{searchUser.username}</span>
                          {searchUser.isFollowing ? (
                            <span className="search-following">Following</span>
                          ) : (
                            <span className="search-email">{searchUser.email}</span>
                          )}
                        </div>
                      </div>
                    ))}
                    {isLoadingMore && (
                      <div className="navbar-search-loading-more">
                        <div className="navbar-search-spinner"></div>
                        <span>Loading more users...</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="search-placeholder">
                    <MagnifyingGlassIcon className="placeholder-icon" />
                    <h3>Search for users</h3>
                    <p>Find users by their username</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Logout Confirmation Modal */}
      {showLogoutModal && (
        <div className="navbar-logout-modal-overlay">
          <div className="navbar-logout-modal-content">
            <div className="navbar-logout-modal-header">
              <h3 className="navbar-logout-modal-title">Logout</h3>
            </div>
            <div className="navbar-logout-modal-body">
              <p>Are you sure you want to logout? You'll need to sign in again to access your account.</p>
            </div>
            <div className="navbar-logout-modal-footer">
              <button
                onClick={handleLogoutCancel}
                className="navbar-logout-modal-button cancel"
              >
                Cancel
              </button>
              <button
                onClick={handleLogoutConfirm}
                className="navbar-logout-modal-button logout-confirm"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}

    </>
  );
};

export default Navbar;